import { AbortError, isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";
import { nanoid } from "nanoid";
import { z } from "zod";

import { NetworkingLayerConfig } from "./config";
import { isFetchNetworkError, waitBeforeNetworkRetry, waitUntilOnline } from "./network-retry";

export type KnownApiHost = "cap-srv" | "cap-code";

export type ApiHost = KnownApiHost | { type: "custom"; host: string };

export type ReplayableRequestBody = Exclude<BodyInit, ReadableStream>;

interface ApiRequestParams {
  host: ApiHost;

  path: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

  data?: Record<string, unknown>;
  body?: ReplayableRequestBody;
  headers?: Record<string, string>;

  clientTrace?: string;

  signal?: AbortSignal;

  assumeUnAuthenticated?: boolean;

  // Set `fetch`'s `keepalive: true` so the request survives the page
  // unload that's about to follow (reload / close / navigation). Use
  // for beacon-style fire-and-forget sends whose payload must reach
  // the server even when the user is leaving. Subject to the Fetch
  // spec 64 KiB per-document keepalive body cap — callers must
  // truncate large payloads themselves.
  keepalive?: boolean;
}

export const createClientTrace = () => `web/${nanoid()}`;

export function applyCapSrvHeaders(headers: Record<string, string>, clientTrace?: string) {
  headers["x-captions-user-timezone"] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  headers["x-captions-client-trace"] = clientTrace ?? createClientTrace();
}

const responseTypeToMethod = {
  "application/json": "json",
  "text/plain": "text",
  "text/html": "text",
  "text/csv": "text",
  "application/octet-stream": "blob",
  "application/protobuf": "blob",
  "application/x-protobuf": "blob",
} as const;

export interface ApiResponse {
  ok: boolean;
  data: unknown;
  status: number;
}

export async function makeApiRequest(
  config: NetworkingLayerConfig,
  {
    host,
    path,
    method,
    data,
    body: explicitBody,
    headers: userHeaders,
    clientTrace,
    signal,
    assumeUnAuthenticated = false,
    keepalive = false,
  }: ApiRequestParams
): Promise<ApiResponse> {
  const urlHost = typeof host === "object" ? host.host : config.getApiHost(host);

  const url = new URL(path, urlHost);

  const configHeaders =
    typeof config.headers === "function" ? config.headers(host, { path, method }) : config.headers;

  const headers: Record<string, string> = {
    ...userHeaders,
    ...configHeaders,
  };

  let accessToken: string | null = null;
  if (!assumeUnAuthenticated) {
    accessToken = await config.getAccessToken();
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }
  }

  if (host === "cap-srv") {
    applyCapSrvHeaders(headers, clientTrace);
  }

  let body = explicitBody;
  if (
    typeof body === "undefined" &&
    (method === "POST" || method === "PUT" || method === "PATCH")
  ) {
    if (data instanceof FormData) {
      body = data;
    } else if (data) {
      body = JSON.stringify(data);
      headers["content-type"] = "application/json";
    }
  }

  const response = await fetchWithAuthRetry(config, {
    url,
    method,
    signal,
    headers,
    body,
    keepalive,
    accessToken,
    assumeUnAuthenticated,
  });

  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return { data: {}, status: response.status, ok: response.ok };
  }

  const contentType = response.headers.get("content-type") ?? "application/json";
  const mediaType = contentType.split(";")[0].trim().toLowerCase();
  const parseFunc = responseTypeToMethod[mediaType as keyof typeof responseTypeToMethod] ?? "json";
  const responseData = await response[parseFunc]();

  const parsedResponse = deserializeResponseFromHost(host, responseData, mediaType);

  return { data: parsedResponse, status: response.status, ok: response.ok };
}

interface FetchWithAuthRetryParams {
  url: URL;
  method: ApiRequestParams["method"];
  signal?: AbortSignal;
  headers: Record<string, string>;
  body?: ReplayableRequestBody;
  keepalive: boolean;
  accessToken: string | null;
  assumeUnAuthenticated: boolean;
}

async function fetchWithAuthRetry(
  config: NetworkingLayerConfig,
  {
    url,
    method,
    signal,
    headers,
    body,
    keepalive,
    accessToken,
    assumeUnAuthenticated,
  }: FetchWithAuthRetryParams
): Promise<Response> {
  const response = await fetchWhenOnline(url, { method, signal, headers, body, keepalive });

  if (!shouldRetryWithRefreshedAccessToken(response, assumeUnAuthenticated, accessToken)) {
    return response;
  }

  const refreshedAccessToken = await config.getAccessToken({
    forceRefresh: true,
    staleAccessToken: accessToken,
  });

  if (!refreshedAccessToken || refreshedAccessToken === accessToken) {
    return response;
  }

  return fetchWhenOnline(url, {
    method,
    signal,
    headers: {
      ...headers,
      Authorization: `Bearer ${refreshedAccessToken}`,
    },
    body,
    keepalive,
  });
}

function shouldRetryWithRefreshedAccessToken(
  response: Response,
  assumeUnAuthenticated: boolean,
  accessToken: string | null
): accessToken is string {
  return response.status === 401 && !assumeUnAuthenticated && !!accessToken;
}

async function fetchWhenOnline(url: URL, init: RequestInit): Promise<Response> {
  while (true) {
    await waitUntilOnline(init.signal);

    try {
      return await fetch(url, init);
    } catch (error) {
      if (init.signal?.aborted) {
        throw new AbortError();
      }

      if (isAbortOrCanceledError(error)) {
        throw error;
      }

      if (!isFetchNetworkError(error)) {
        throw error;
      }

      const shouldRetry = await waitBeforeNetworkRetry(init.signal);
      if (!shouldRetry) {
        throw error;
      }
    }
  }
}

function deserializeResponseFromHost(host: ApiHost, data: unknown, contentType: string) {
  if (host === "cap-srv" && contentType === "application/json") {
    const { success, ...rest } = baseCapSrvResponseSchema.parse(data).result;

    if (success) {
      return rest.data;
    }

    return rest;
  }

  return data;
}

export const baseCapSrvResponseSchema = z
  .object({
    result: z
      .object({
        data: z.any().optional(),
        success: z.boolean(),
      })
      .passthrough(),
  })
  .passthrough();
